import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { WBBox, WBDataGrid, WBSelect, WBTextField } from '@admiin-com/ds-web';
import { MenuItem } from '@mui/material';
import {
  EntityClientsStatus,
  type Entity,
  ModelSortDirection,
} from '@admiin-com/ds-graphql';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../navigation/paths';

export const SEARCH_ENTITIES = gql(/* GraphQL */ `
  query SearchEntities(
    $searchName: String
    $clientsStatus: EntityClientsStatus
    $sortBy: String
    $sortDirection: ModelSortDirection
  ) {
    searchEntities(
      searchName: $searchName
      clientsStatus: $clientsStatus
      sortBy: $sortBy
      sortDirection: $sortDirection
    ) {
      items {
        id
        name
        taxNumber
        contact {
          firstName
          lastName
          email
          phone
        }
        clientsStatus
      }
      total
    }
  }
`);

const Entities = () => {
  const { t } = useTranslation('entities');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [clientsStatus, setClientsStatus] = useState<EntityClientsStatus | ''>(
    ''
  );
  const [sortOption, setSortOption] = useState<
    'searchNameAsc' | 'searchNameDesc' | 'createdAtDesc' | 'createdAtAsc'
  >('searchNameAsc');

  const sortOptionsMap: Record<
    string,
    { sortBy: string; sortDirection: ModelSortDirection }
  > = {
    searchNameAsc: {
      sortBy: 'searchName.keyword',
      sortDirection: ModelSortDirection.ASC,
    },
    searchNameDesc: {
      sortBy: 'searchName.keyword',
      sortDirection: ModelSortDirection.DESC,
    },
    createdAtDesc: {
      sortBy: 'createdAt',
      sortDirection: ModelSortDirection.DESC,
    },
    createdAtAsc: {
      sortBy: 'createdAt',
      sortDirection: ModelSortDirection.ASC,
    },
  };

  const { sortBy, sortDirection } = sortOptionsMap[sortOption];

  const { data, loading } = useQuery(SEARCH_ENTITIES, {
    variables: {
      searchName: search,
      clientsStatus: clientsStatus || null,
      sortBy,
      sortDirection,
    },
  });

  const rows = (data?.searchEntities?.items as Entity[]) ?? [];

  const columns = [
    { field: 'id', headerName: t('id'), width: 200 },
    { field: 'name', headerName: t('name'), flex: 1 },
    { field: 'taxNumber', headerName: t('taxNumber'), flex: 1 },
    {
      field: 'contactFirstName',
      headerName: t('contactFirstName'),
      flex: 1,
      valueGetter: (_value: any, params: any) =>
        params?.contact?.firstName || '',
    },
    {
      field: 'contactLastName',
      headerName: t('contactLastName'),
      flex: 1,
      valueGetter: (_value: any, params: any) =>
        params?.contact?.lastName || '',
    },
    {
      field: 'contactEmail',
      headerName: t('contactEmail'),
      flex: 1,
      valueGetter: (_value: any, params: any) => params?.contact?.email || '',
    },
    {
      field: 'contactPhone',
      headerName: t('contactPhone'),
      flex: 1,
      valueGetter: (_value: any, params: any) => params?.contact?.phone || '',
    },
    { field: 'clientsStatus', headerName: t('clientsStatus'), flex: 1 },
  ];

  return (
    <WBBox p={2}>
      <WBSelect
        label={t('clientsStatus') as string}
        value={clientsStatus}
        onChange={(e) =>
          setClientsStatus(e.target.value as EntityClientsStatus | '')
        }
        fullWidth
      >
        <MenuItem value="">{t('allStatus')}</MenuItem>
        {Object.values(EntityClientsStatus).map((status) => (
          <MenuItem key={status} value={status}>
            {t(status)}
          </MenuItem>
        ))}
      </WBSelect>
      <WBTextField
        label={t('searchLabel')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        margin="normal"
      />
      <WBSelect
        label={t('sortBy') as string}
        value={sortOption}
        onChange={(e) =>
          setSortOption(
            e.target.value as
              | 'searchNameAsc'
              | 'searchNameDesc'
              | 'createdAtDesc'
              | 'createdAtAsc'
          )
        }
        fullWidth
        sx={{ mt: 2 }}
      >
        <MenuItem value="createdAtDesc">{t('newest')}</MenuItem>
        <MenuItem value="createdAtAsc">{t('oldest')}</MenuItem>
        <MenuItem value="searchNameAsc">{t('aToZ')}</MenuItem>
        <MenuItem value="searchNameDesc">{t('zToA')}</MenuItem>
      </WBSelect>
      <WBDataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        autoHeight
        onRowClick={(params) => navigate(PATHS.entity(params.row.id))}
      />
    </WBBox>
  );
};

export default Entities;
